import { type OpeningStock } from "@artisancode/api-types";
import { ArrowLeft, CheckCircle2, Package, Save, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DatePicker } from "@/components/shared/date-picker";
import { SearchableSelect } from "@/components/shared/searchable-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useHasPermission } from "@/hooks/use-auth";
import { useOpeningStock } from "@/hooks/use-opening-stock";
import { formatCurrency } from "@/lib/utils";

import { OpeningStockLines } from "./opening-stock-lines";
import { formatDate } from "./opening-stock-utils";
import { useOpeningStockSession } from "./use-opening-stock-session";

export function OpeningStockSessionPage() {
  const { id } = useParams<{ id: string }>();
  const docId = id && id !== "new" ? id : undefined;
  const { data: doc, isLoading } = useOpeningStock(docId);

  if (docId && isLoading) {
    return (
      <div className="py-24 text-center text-xs text-muted-foreground">
        Memuat dokumen Stok Awal...
      </div>
    );
  }
  return <OpeningStockSession key={docId ?? "new"} docId={docId} doc={doc} />;
}

function OpeningStockSession({
  docId,
  doc,
}: {
  docId?: string;
  doc?: OpeningStock;
}) {
  const navigate = useNavigate();
  const canComplete = useHasPermission("opening_stocks.complete");
  const canDelete = useHasPermission("opening_stocks.delete");
  const s = useOpeningStockSession(docId, doc);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const locked = doc?.status === "completed";

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={() => navigate("/opening-stocks")}
          >
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight">
                {doc?.code || "Stok Awal Baru"}
              </h1>
              <Badge
                variant={locked ? "default" : "secondary"}
                className="text-[10px]"
              >
                {locked ? "Selesai" : "Draft"}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {locked
                ? `Diselesaikan oleh ${doc?.completedByName ?? "-"} pada ${formatDate(doc?.completedAt)}`
                : "Stok yang sudah ada di gudang dan bar saat mulai memakai aplikasi, beserta harganya."}
            </p>
          </div>
        </div>

        {!locked && (
          <div className="flex flex-wrap items-center gap-2">
            {docId && canDelete && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="mr-1.5 size-4" />
                Hapus Draft
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={s.saveDraft}
              disabled={s.isSaving}
            >
              <Save className="mr-1.5 size-4" />
              Simpan Draft
            </Button>
            {canComplete && (
              <Button
                size="sm"
                onClick={() => setConfirmOpen(true)}
                disabled={s.lines.length === 0 || s.isSaving || s.isCompleting}
              >
                <CheckCircle2 className="mr-1.5 size-4" />
                Selesaikan
              </Button>
            )}
          </div>
        )}
      </div>

      <Card className="shadow-xs">
        <CardContent className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-3 sm:p-6">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Tanggal</Label>
            <DatePicker value={s.date} onChange={s.setDate} disabled={locked} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Catatan (Opsional)</Label>
            <Input
              placeholder="Misal: Stok awal saat mulai pakai aplikasi"
              value={s.note}
              disabled={locked}
              onChange={(e) => s.setNote(e.target.value)}
              className="h-9 text-xs"
            />
          </div>
          <div className="flex flex-col justify-end space-y-1.5">
            <span className="text-xs text-muted-foreground">Total Nilai</span>
            <span className="text-xl font-bold text-primary">
              {formatCurrency(s.totalCost)}
            </span>
          </div>
        </CardContent>
      </Card>

      {!locked && (
        <Card className="border-dashed shadow-xs">
          <CardContent className="p-4">
            <SearchableSelect
              placeholder="Cari bahan baku atau produk untuk ditambahkan..."
              options={s.availableOptions}
              value=""
              onChange={s.addLine}
            />
          </CardContent>
        </Card>
      )}

      <Card className="shadow-xs">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">
            Daftar Barang ({s.lines.length})
          </CardTitle>
          <CardDescription className="text-xs">
            Gudang = stok cadangan, Bar = stok yang sudah siap dipakai. Isi
            sesuai lokasi fisik barangnya; barang yang sama jangan dihitung di
            dua kolom. Barang beli hanya di gudang, bahan resep hanya di bar
            (harganya dari barang beli pasangannya). Harga diisi per satuan yang
            tertera, bukan per pack/slop.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0 sm:p-6 sm:pt-0">
          {s.lines.length === 0 ? (
            <div className="space-y-2 py-12 text-center text-xs text-muted-foreground">
              <Package className="mx-auto size-8 text-muted-foreground/40" />
              <p>Belum ada barang di dokumen ini.</p>
            </div>
          ) : (
            <OpeningStockLines
              lines={s.lines}
              locked={locked}
              onChange={s.updateLine}
              onRemove={s.removeLine}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Selesaikan Stok Awal?</DialogTitle>
            <DialogDescription>
              {s.lines.length} barang senilai {formatCurrency(s.totalCost)}{" "}
              masuk ke stok, dan harganya jadi harga awal barang. Dokumen tidak
              bisa diubah lagi.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Batal
            </Button>
            <Button
              disabled={s.isCompleting || s.isSaving}
              onClick={async () => {
                if (await s.complete()) setConfirmOpen(false);
              }}
            >
              {s.isCompleting ? "Memproses..." : "Ya, Selesaikan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Hapus draft Stok Awal?"
        description="Draft ini akan dihapus."
        confirmLabel="Hapus Draft"
        onConfirm={s.remove}
        isConfirming={s.isDeleting}
      />
    </div>
  );
}
